// ── reports-columns: the Reports list table columns (Wave 7) ────────────────
// Each row links to the report viewer (/reports/:id). The Audience badge uses
// the D-1 taxonomy labels; the Status uses the per-type independent generation
// status. Enum display is humanized; a missing optional field renders "—". Ids
// are STRINGS — passed VERBATIM into the viewer link. This table shows report
// METADATA + STATUS only — never report content (summary/highlights/HTML).

import { Badge, StatusDot } from "@vjal/ui";
import { Link } from "react-router-dom";

import type { CoachingArtifactDto, CoachingReportType } from "../../services/coaching-dtos";
import { reportTypeLabels } from "../../domain/report-taxonomy";
import { orDash, reportStatusLabel, reportStatusTone } from "./reports-helpers";

export type ReportRow = CoachingArtifactDto & Record<string, unknown>;

function audienceTone(type: CoachingReportType | undefined): "success" | "info" | "warning" {
  if (type === "team_admin") return "success";
  if (type === "coach_feedback") return "info";
  return "warning";
}

function reportDisplayTitle(row: ReportRow): string {
  const trimmed = (row.title ?? "").trim();
  if (trimmed.length > 0) return trimmed;
  const session = (row.session_title ?? "").trim();
  return session.length > 0 ? `${session} report` : `Report ${row.id}`;
}

export function buildReportsCollectionColumns() {
  return [
    {
      id: "title",
      header: "Report",
      sortable: true,
      sortAccessor: (row: ReportRow) => reportDisplayTitle(row),
      searchValue: (row: ReportRow) => `${row.title ?? ""} ${row.session_title ?? ""} ${row.cohort_name ?? ""}`,
      priority: 1,
      cell: (row: ReportRow) => (
        <Link
          to={`/reports/${encodeURIComponent(row.id)}`}
          className="font-medium text-link no-underline hover:underline"
          data-exact-set-name="Open report">
          {reportDisplayTitle(row)}
        </Link>
      )
    },
    {
      id: "cohort_name",
      header: "Cohort",
      sortable: true,
      sortAccessor: (row: ReportRow) => row.cohort_name ?? "",
      priority: 2,
      cell: (row: ReportRow) => orDash(row.cohort_name)
    },
    {
      id: "report_type",
      header: "Audience",
      sortable: true,
      sortAccessor: (row: ReportRow) => row.report_type ?? "",
      priority: 2,
      cell: (row: ReportRow) => {
        const type = row.report_type;
        return (
          <span data-report-type={type ?? "team_admin"}>
            <Badge variant={audienceTone(type)}>{type ? reportTypeLabels[type] : "Team / company"}</Badge>
          </span>
        );
      }
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      sortAccessor: (row: ReportRow) => row.status ?? "",
      priority: 2,
      cell: (row: ReportRow) => (
        <StatusDot tone={reportStatusTone(row.status) === "danger" ? "err" : "info"} label={reportStatusLabel(row.status)}  />
      )
    }
  ];
}
